import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FiUser, FiLock, FiEye, FiEyeOff, FiAlertCircle } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { getEmpresa } from '../services/api';
import '../styles/Login.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

export default function LoginPage() {
  const { login, isAuthenticated } = useAuth();
  const navigate  = useNavigate();
  const location  = useLocation();
  const from      = location.state?.from?.pathname || '/';

  const [form, setForm]         = useState({ nombre_usuario: '', contrasena: '' });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [empresa, setEmpresa]   = useState(null);

  useEffect(() => {
    if (isAuthenticated) navigate(from, { replace: true });
  }, [isAuthenticated, navigate, from]);

  useEffect(() => {
    getEmpresa()
      .then(r => setEmpresa(r.data.empresa))
      .catch(() => {});
  }, []);

  const handleChange = (e) => {
    setError('');
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nombre_usuario.trim() || !form.contrasena) {
      setError('Ingresa tu usuario y contraseña.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await login(form.nombre_usuario.trim(), form.contrasena);
      navigate(from, { replace: true });
    } catch (err) {
      setError(err.response?.data?.error || 'Error al iniciar sesión. Intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  const logoSrc      = empresa?.logo         ? `${API_URL}${empresa.logo}`         : null;
  const fondoInicio  = empresa?.fondo_inicio  ? `${API_URL}${empresa.fondo_inicio}` : `${process.env.PUBLIC_URL}/fondo_inicio.png`;
  const nombreEmpresa = empresa?.razon_social || process.env.REACT_APP_NAME || 'Sistema de Gestión';

  return (
    <div className="login-page">
      {/* Panel izquierdo — solo imagen de fondo */}
      <div className="login-panel-left" style={{
        backgroundImage: `url(${fondoInicio})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}>
        <div className="login-left-credit">
          Desarrollado por <strong>Devel@perF@xy</strong> — www.developerfoxy.com
        </div>
      </div>

      {/* Panel derecho — formulario */}
      <div className="login-panel-right">
        <div className="login-form-container">

          {/* Logo y nombre de empresa */}
          <div className="login-brand-top">
            {logoSrc ? (
              <img
                src={logoSrc}
                alt={nombreEmpresa}
                className="login-logo-top"
                onError={e => { e.currentTarget.style.display = 'none'; }}
              />
            ) : (
              <div className="login-logo-fallback-top">MS</div>
            )}
            <div className="login-brand-name-top">{nombreEmpresa}</div>
            <div className="login-brand-tagline-top">Sistema de Gestión</div>
          </div>

          <h1 className="login-form-title">Iniciar sesión</h1>
          <p className="login-form-subtitle">Ingresa tus credenciales para continuar</p>

          <form className="login-form" onSubmit={handleSubmit} noValidate>
            {error && (
              <div className="login-error">
                <FiAlertCircle size={15} />
                {error}
              </div>
            )}

            <div className="login-field">
              <label htmlFor="nombre_usuario">Usuario</label>
              <div className="login-input-wrapper">
                <span className="login-input-icon"><FiUser size={15} /></span>
                <input
                  id="nombre_usuario"
                  name="nombre_usuario"
                  type="text"
                  className="login-input"
                  placeholder="Nombre de usuario"
                  value={form.nombre_usuario}
                  onChange={handleChange}
                  autoComplete="off"
                  autoFocus
                  disabled={loading}
                />
              </div>
            </div>

            <div className="login-field">
              <label htmlFor="contrasena">Contraseña</label>
              <div className="login-input-wrapper">
                <span className="login-input-icon"><FiLock size={15} /></span>
                <input
                  id="contrasena"
                  name="contrasena"
                  type={showPass ? 'text' : 'password'}
                  className="login-input"
                  placeholder="••••••••"
                  value={form.contrasena}
                  onChange={handleChange}
                  autoComplete="new-password"
                  disabled={loading}
                />
                <button
                  type="button"
                  className="login-input-toggle"
                  onClick={() => setShowPass(v => !v)}
                  tabIndex={-1}
                >
                  {showPass ? <FiEyeOff size={15} /> : <FiEye size={15} />}
                </button>
              </div>
            </div>

            <button type="submit" className="login-btn" disabled={loading}>
              {loading ? (
                <span className="login-btn-spinner">
                  <span className="spinner" />
                  Verificando...
                </span>
              ) : (
                'Ingresar al sistema'
              )}
            </button>
          </form>

          <div className="login-form-footer">
            <span>{nombreEmpresa}</span>
            {empresa?.ruc && <> &nbsp;·&nbsp; RUC {empresa.ruc}</>}
          </div>
        </div>
      </div>
    </div>
  );
}
